import { useField } from 'formik'
import { CrossPostAccount, CrossPostAccountsInput } from './CrossPostAccountsInput/CrossPostAccountsInput'
import { useWizardSchema } from '../WizardContext'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { integrationToName } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { Field } from 'components/Wizards/Field/Field'
import { Button } from 'blaze-ui/Button'
import { LightningFilled } from 'blaze-ui/icons'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import styles from './CrossPostAccountsField.module.scss'
import { useExternalIntegrationsRealtime } from 'components/Wizards/ContentPlanning/ExternalIntegrationField/useExternalIntegrationsRealtime'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useInitiateOauth } from 'hooks/useInitiateOauth'

export const CrossPostAccountsField = ({ isDesign }: { isDesign?: boolean }) => {
  const schema = useWizardSchema()
  const { data: integrationsData, isLoading: externalIntegrationsAreLoading, refetch } = useExternalIntegrations()
  const workspace = useCurrentWorkspace()
  const { initiateOauth } = useInitiateOauth()

  useExternalIntegrationsRealtime({
    workspaceId: workspace?.id,
    onMessage: () => refetch(),
  })

  const { connect } = useConnectAccountPopup({ isSingleSelect: true })

  const openConnectAccountPopup = async () => {
    if (!requiredProvider) return

    const popup = await initiateOauth(requiredProvider, undefined, '_blank', 'popup=true,width=600,height=780')
    popup && connect(popup, {})
  }

  const [field, meta, helpers] = useField<CrossPostAccount[]>({
    name: 'cross_post_accounts',
    validate: (accounts: CrossPostAccount[]) => {
      const channel = 'type' in schema.meta ? schema.meta.type : (schema as MultiStepSchema).meta.channel
      const requiredProvider = providerFromChannelType[channel as ChannelType]

      if (!requiredProvider || !integrationsData?.external_integrations) return

      // Check if user has any accounts for the required provider
      const hasAccountsForProvider = integrationsData.external_integrations.some(
        (integration) => integration.provider === requiredProvider,
      )

      if (!hasAccountsForProvider) return

      // Only validate if user has accounts for this provider
      const hasMatchingProvider = accounts.some((account) => account.provider === requiredProvider)

      if (!hasMatchingProvider) {
        return `You have to post to at least one of your ${
          integrationToName[requiredProvider as ExternalIntegrationEnum]
        } accounts to continue`
      }
    },
  })

  const channel = 'type' in schema.meta ? schema.meta.type : (schema as MultiStepSchema).meta.channel
  const requiredProvider = providerFromChannelType[channel as ChannelType]

  const hasMatchingIntegrations = integrationsData?.external_integrations?.some(
    (integration) => integration.provider === requiredProvider,
  )

  return (
    <Field
      label="Posting"
      description={
        hasMatchingIntegrations
          ? 'Select accounts you would like to post to'
          : `You don't have any ${
              integrationToName[requiredProvider as ExternalIntegrationEnum]
            } integrations connected at the moment. You can still generate your post and then setup your integration.`
      }
    >
      {!externalIntegrationsAreLoading && !hasMatchingIntegrations && workspace && requiredProvider ? (
        <div className={styles.noIntegrationContainer}>
          <div>
            <Button size="lg" variant="secondary" frontIcon={LightningFilled} onPress={openConnectAccountPopup}>
              Connect Account
            </Button>
          </div>
        </div>
      ) : (
        <CrossPostAccountsInput
          initialProvider={providerFromChannelType[channel as ChannelType]}
          value={field.value}
          onChange={(newValue: CrossPostAccount[]) => helpers.setValue(newValue)}
          isDesign={isDesign}
          errorMessage={meta.error}
        />
      )}
    </Field>
  )
}
